import { CommonModule } from "@angular/common";
import { Component, ElementRef, Input, OnChanges, TemplateRef, ViewChild } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ColourUtilities } from "@common/lib/utilities/colour-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { buttonPreset } from "@common/ux/button/button-preset";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { AdaptMenuModule } from "@common/ux/menu/menu.module";
import { AdaptStopPropagationDirective } from "@common/ux/stop-propagation.directive";
import { AdaptTooltipModule } from "@common/ux/tooltip.directive/tooltip.module";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { ConfigurationAuthService } from "@org-common/lib/configuration/configuration-auth.service";
import { AdaptDirectorySharedModule } from "@org-common/lib/directory-shared/directory-shared.module";
import { AdaptLabellingModule } from "@org-common/lib/labelling/labeling.module";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsUiService } from "@org-common/lib/teams/common-teams-ui.service";
import { AdaptTeamDashboardSharedModule } from "@org-common/lib/teams/team-dashboard-shared/team-dashboard-shared.module";
import { TeamHierarchyService } from "@org-common/lib/teams/team-hierarchy.service";
import { CollapseDirective } from "ngx-bootstrap/collapse";
import { SortablejsDirective } from "nxt-sortablejs";
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject } from "rxjs";
import { filter, map, startWith, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { Options as SortablejsOptions } from "sortablejs";
import { OrganisationMapService } from "../organisation-map.service";

@Component({
    selector: "adapt-team-card",
    templateUrl: "./team-card.component.html",
    styleUrls: ["./team-card.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        AdaptMenuModule,
        AdaptTooltipModule,
        AdaptStopPropagationDirective,
        SortablejsDirective,
        CollapseDirective,
        AdaptTeamDashboardSharedModule,
        AdaptDirectorySharedModule,
        AdaptLabellingModule,
    ],
    host: {
        "[attr.data-team-id]": "teamId",
        "(click)": "handleCardClick($event)",
        "[style.--column-span]": "columnSpan",
        "[class.team-card-tinted]": "teamBackgroundIsTinted",
        "[style.background-color]": "teamBackgroundColour",
        "[style.--team-card-background-color]": "teamBackgroundColour",
        "[style.--team-card-text-color]": "teamTextColour",
        "[style.--team-card-composited-background-color]": "compositedBackgroundString",
    },
})
export class TeamCardComponent extends BaseComponent implements OnChanges {
    @Input() public set team(value: Team | undefined) {
        if (value) {
            this.team$.next(value);
        } else {
            this.teamId = undefined;
        }
    }

    public team$ = new ReplaySubject<Team>(1);
    public teamId?: number;
    public teamBackgroundColour?: string;
    public teamBackgroundIsTinted = false;
    public teamTextColour?: string;

    // Parent card's fully composited (opaque) background to blend into.
    @Input() public parentBackground?: Uint8ClampedArray;

    // This card's fully composited background, passed to children.
    public compositedBackground?: Uint8ClampedArray;
    public compositedBackgroundString?: string;

    @Input() public showChildren = false;
    @Input() public childrenExpanded = false;
    @Input() public showChildCount = false;
    @Input() public filterTeams?: (teams: Team[]) => Team[] | Observable<Team[]>;
    @Input() public set includeInactiveTeams(value: boolean) {
        this.includeInactiveTeams$.next(value);
    }
    @Input() public level = 1;
    @Input() public currentViewLevel = 0;

    @Input() public teamLocation?: TeamLocation;
    @Input() public isEditing = false;
    @Input() public teamContentTemplate?: TemplateRef<{ $implicit: Team; level: number }>;

    @ViewChild("reorderHandle") public reorderHandle?: ElementRef<HTMLDivElement>;
    @ViewChild("childrenContainer") public childrenContainer?: ElementRef<HTMLDivElement>;

    public childTeams$: Observable<TeamLocation[]>;
    public includeInactiveTeams$ = new BehaviorSubject(false);

    public sortableOptions: SortablejsOptions = { ...this.orgMapService.defaultSortableOptions };
    public draggingTeamCanBeDroppedIntoParentTeam = true;

    public itemMenu: IAdaptMenuItem[] = [];
    public menuVisible = false;

    // type doesn't matter, allows us to chain this easier
    private triggerChildrenUpdate$ = new BehaviorSubject<unknown>(undefined);

    public constructor(
        protected elementRef: ElementRef,
        private orgMapService: OrganisationMapService,
        private teamsUiService: CommonTeamsUiService,
        private authorisationService: AuthorisationService,
        teamService: CommonTeamsService,
        rxjsBreezeService: RxjsBreezeService,
        teamHierarchyService: TeamHierarchyService,
    ) {
        super(elementRef);

        this.childTeams$ = combineLatest([
            this.triggerChildrenUpdate$,
            this.team$,
            this.includeInactiveTeams$,
        ]).pipe(
            switchMap(([_, team, includeInactiveTeams]) => this.orgMapService.getLocationItems({ parentTeamId: team.teamId }, includeInactiveTeams)),
            // apply the team filter to just the team entities, then filter the locations based on the included teams
            switchMap((teamLocations) => teamService.applyTeamsFilter(teamLocations.map((t) => t.team), this.filterTeams).pipe(
                map((filteredTeams) => teamLocations.filter((teamLocation) => filteredTeams.includes(teamLocation.team))),
            )),
        );

        this.team$.pipe(
            switchMap((team) => combineLatest([
                rxjsBreezeService.entityPropertyChangedWithInitialEntity(team),
                rxjsBreezeService.entityPropertyChangedWithInitialEntity(team.teamTypeConfiguration),
                // update cards when any backgroundColour changes. an indirect parent update could cause the colours to need updating.
                rxjsBreezeService.entityTypePropertyChanged(TeamTypeConfiguration).pipe(startWith(undefined)),
            ])),
            tap(async ([team, _thisTeamTypeConfig, _anyTeamTypeConfig]) => {
                this.teamId = team.teamId;
                const parents = await teamHierarchyService.promiseToGetImmediateParents(team);

                let backgroundColour = team.extensions.getCustomData()?.backgroundColour;
                if (!backgroundColour) {
                    backgroundColour = team.teamTypeConfiguration?.backgroundColour;
                }

                const directParent = this.teamLocation?.parentTeam
                    ? parents.find((p) => p.entity?.teamId === this.teamLocation?.parentTeam?.teamId)
                    ?? parents[0]
                    : parents[0];

                const parentColour = directParent?.entity?.extensions.getBackgroundColour();
                const hasTeamParent = directParent?.entity !== undefined;

                if ((!backgroundColour && hasTeamParent) || (parentColour === backgroundColour)) {
                    this.teamBackgroundIsTinted = true;
                    this.teamBackgroundColour = undefined;
                } else {
                    this.teamBackgroundIsTinted = false;
                    this.teamBackgroundColour = backgroundColour;
                }

                const el = this.elementRef!.nativeElement as HTMLElement;
                const base = this.teamBackgroundColour
                    ?? parentColour
                    ?? ColourUtilities.getCssVariableValue("--zone-background-colour", el);

                if (base) {
                    // Build the background behind this card: either the
                    // parent card's already-composited background or the
                    // zone background for top-level cards.
                    const behind = this.parentBackground
                        ?? ColourUtilities.colourToRGBA(
                            ColourUtilities.getCssVariableValue("--zone-background-colour", el),
                        );

                    // Tinted cards use rgba(255,255,255,0.5) just like the CSS
                    // class; non-tinted cards use their own background colour.
                    const fgRgba = this.teamBackgroundIsTinted
                        ? new Uint8ClampedArray([255, 255, 255, 128])
                        : ColourUtilities.colourToRGBA(base, el);
                    this.compositedBackground = ColourUtilities.blendOver(fgRgba, behind);
                    this.compositedBackgroundString = `rgb(${this.compositedBackground.slice(0, 3).join(",")})`;
                    this.teamTextColour = ColourUtilities.contrastTextColour(
                        ColourUtilities.luminance(this.compositedBackground),
                    );
                } else {
                    this.teamTextColour = "var(--bs-body-color)";
                }
            }),
            this.takeUntilDestroyed(),
        ).subscribe();

        this.team$.pipe(
            switchMap((team) => this.orgMapService.emitWhenLocationShouldBeFetched({ parentTeamId: team.teamId })),
            this.takeUntilDestroyed(),
        ).subscribe(this.triggerChildrenUpdate$);

        rxjsBreezeService.entityTypeChanged(Team).pipe(
            withLatestFrom(this.team$),
            filter(([changedTeam, currentTeam]) => changedTeam.teamId === currentTeam.teamId),
            this.takeUntilDestroyed(),
        ).subscribe(this.triggerChildrenUpdate$);

        rxjsBreezeService.entityTypeChanged(TeamTypeConfiguration).pipe(
            withLatestFrom(this.team$),
            filter(([changedConfig, currentTeam]) => changedConfig.teamTypeConfigurationId === currentTeam.teamTypeConfigurationId),
            this.takeUntilDestroyed(),
        ).subscribe(this.triggerChildrenUpdate$);

        this.orgMapService.mapEntityDragging$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            // team just started or stopped dragging
            this.draggingTeamCanBeDroppedIntoParentTeam = this.teamId
                ? this.orgMapService.draggingTeamCanBeDroppedIntoParentTeam(this.teamId)
                : true;
        });
    }

    public get columnSpan() {
        if (this.teamLocation?.span !== undefined) {
            if (this.teamLocation.span === -1) {
                return "-1 / 1";
            }

            return `span ${this.teamLocation.span}`;
        }

        return undefined;
    }

    public async ngOnChanges() {
        this.sortableOptions = { ...this.sortableOptions, disabled: !this.isEditing };
        if (this.isEditing) {
            await this.updateMenu();
        }
    }

    public getCollapseTooltip(childCount: number) {
        return this.isEditing && childCount === 0
            ? "Click to add child teams"
            : `Click to ${this.childrenExpanded ? "hide" : "show"} child teams`;
    }

    public teamHasChildren(teamId: number, includeInactiveTeams = false) {
        return this.orgMapService.teamHasChildren(teamId, includeInactiveTeams);
    }

    public editTeamLocations(team: Team) {
        return this.teamsUiService.editTeamLocations(team);
    }

    public deleteTeamLocation(teamLocation: TeamLocation) {
        return this.teamsUiService.deleteTeamLocation(teamLocation);
    }

    public handleCardClick(event: PointerEvent) {
        // don't collapse/expand when clicking the reorder handle
        // or clicking child cards
        if (event.target !== this.reorderHandle?.nativeElement && !this.childrenContainer?.nativeElement.contains(event.target as Element)) {
            this.childrenExpanded = !this.childrenExpanded;
        }
    }

    private async updateMenu() {
        const team = await firstValueFrom(this.team$);
        const canConfigureOrganisation = await this.authorisationService.promiseToGetHasAccess(
            ConfigurationAuthService.ConfigureOrganisation,
        );

        const items: IAdaptMenuItem[] = [
            {
                text: `Edit locations for this ${team?.teamTypeName}`,
                icon: buttonPreset.edit.iconClass + " fa-fw",
                onClick: () => this.editTeamLocations(team).subscribe(() => this.updateMenu()),
            },
            {
                text: "Adjust size on map",
                visible: !!this.teamLocation && this.level <= 2,
                icon: "fal fa-fw fa-columns",
                items: [undefined, 2, 3, 4, -1].map((span) => {
                    return {
                        text: !span
                            ? "Default"
                            : span === -1
                                ? `Full width`
                                : `Span ${span} columns`,
                        isChecked: this.teamLocation?.span === span || (!span && !this.teamLocation?.span),
                        onIsCheckedChange: (isChecked) => {
                            if (this.teamLocation) {
                                this.teamLocation.span = isChecked ? span : undefined;
                                this.orgMapService.emitMapEntityChanged([this.teamLocation]);
                                this.updateMenu();
                            }
                        },
                    } as IAdaptMenuItem;
                }),
            },
        ];

        if (canConfigureOrganisation) {
            items.push({
                text: `Change appearance for this ${team?.teamTypeName}`,
                icon: "fal fa-fw fa-palette",
                onClick: () => this.teamsUiService.changeTeamAppearance(team).subscribe(() => this.updateMenu()),
            });

            if (team.teamTypeConfiguration) {
                items.push({
                    text: `Change appearance for all ${team?.teamTypeName}s`,
                    icon: "fal fa-fw fa-layer-group",
                    onClick: () => this.teamsUiService.changeTeamAppearance(team.teamTypeConfiguration).subscribe(() => this.updateMenu()),
                    separatorTop: true,
                });
            }
        }

        items.push({
            text: "Delete this location",
            visible: !!this.teamLocation,
            icon: buttonPreset.delete.iconClass + " fa-fw text-danger",
            onClick: () => this.deleteTeamLocation(this.teamLocation!).subscribe(() => this.updateMenu()),
            separatorTop: true,
        });

        this.itemMenu = [{
            text: "",
            icon: MenuComponent.SmallRootMenu.icon,
            padding: false,
            items,
        }];
    }
}
